/**
 * usePreselectedPlan
 *
 * Reads the `?plan=` query parameter, resolves it to a plan, populates the
 * purchase context, and advances straight to the Review step.
 *
 * Deep-links like:
 *   /subscriptions?plan=membership-5&tenant-handle=myclubhandle
 *   /subscriptions?plan=pass-12&tenant-handle=myclubhandle&club-id=6
 *
 * **Two resolution paths.** The available-subscriptions list is checked first,
 * which covers every listed plan (and therefore every promo link) with no extra
 * request. When the id is absent from that list, the plan is either UNLISTED —
 * off the listing by design, reachable only by this link — or gone. Only then do
 * we fetch it by id.
 *
 * **Mounted for the whole funnel, not inside step 0.** It used to live in
 * `SubscriptionSelectionStep`, which unmounts the moment the step flips to
 * Review. That was survivable when resolution was a synchronous array lookup; it
 * is not survivable with a fetch in flight, which would be dropped along with
 * the `hasApplied` ref.
 *
 * **Guards:**
 * - Applies at most once (ref flag), and marks itself done on EVERY terminal
 *   outcome — not just success — so a miss cannot re-run on each dep change.
 * - Skips if a plan is already selected (user is mid-flow).
 * - Every failure is silent: an unresolvable link falls back to the normal
 *   selection step with no error copy, so the id space reveals nothing.
 *
 * @module subscription/hooks
 */

import { useEffect, useMemo, useRef } from 'react';
import { useTenantSearchParams } from '~/lib/navigation';
import { parseCompositeId } from '~/lib/utils/subscriptionIdUtils';
import { QueryParams } from '~/lib/queryParams';
import { useSubscriptionPurchase } from '../context/SubscriptionPurchaseContext';
import { useSubscriptionStep, SUBSCRIPTION_STEPS } from '../context/SubscriptionStepContext';
import { useAvailableSubscriptions } from './useAvailableSubscriptions';
import { usePlanDetails } from './usePlanDetails';
import { formatMembershipPlan, formatPassPlan } from '../utils/formatPlan';

/**
 * Hook — reads `?plan=` and pre-selects the plan, jumping to Review.
 *
 * Call it ONCE, inside both providers and outside the step manager.
 *
 * @returns `isResolving` — true while a deep link is still being resolved, so
 *   the selection step can hold its skeleton instead of flashing an empty grid
 *   before the jump to Review.
 */
export function usePreselectedPlan(): { isResolving: boolean } {
  const [searchParams] = useTenantSearchParams();
  const { selectedSubscription, setSelectedSubscription, setSelectedTab } = useSubscriptionPurchase();
  const { goToStep } = useSubscriptionStep();
  const { memberships, passes, isLoading: listLoading } = useAvailableSubscriptions();

  const planParam = searchParams.get(QueryParams.PLAN);
  // Memoised because it is an effect dependency and a query-key input below: a
  // fresh object every render re-runs the effect body and defeats the identity
  // checks React Query does on its options (docs/conventions/react-state.md).
  const parsed = useMemo(() => (planParam ? parseCompositeId(planParam) : null), [planParam]);

  // Prevent re-running once we have reached ANY terminal outcome.
  const hasApplied = useRef(false);
  // Set when the next render with a non-null selectedSubscription should advance.
  // Necessary because `setSelectedSubscription` is async — a `goToStep` in the
  // same effect would still see the old (null) value and fail `canGoToStep`.
  const shouldAdvance = useRef(false);

  // Resolution path 1: the plan is in the list we already fetched. Covers every
  // listed plan, and therefore every promo link, at zero extra cost.
  const listMatch =
    parsed && !listLoading
      ? parsed.type === 'membership'
        ? memberships.find((m) => m.id === parsed.id)
        : passes.find((p) => p.id === parsed.id)
      : undefined;

  // Resolution path 2: it is not in the list, so it is either unlisted or gone.
  // DERIVED, not stored — a `setNeedsDetail` in the effect below would be a
  // setState-in-effect cascade, and everything it would encode is already known
  // during render.
  const needsDetail = !!parsed && !listLoading && !listMatch && !selectedSubscription;

  const { plan: fetchedPlan, isPending: detailPending, settled: detailSettled } =
    usePlanDetails({ parsed, enabled: needsDetail });

  // Effect 1: once context holds the plan, advance to Review.
  useEffect(() => {
    if (shouldAdvance.current && selectedSubscription) {
      shouldAdvance.current = false;
      goToStep(SUBSCRIPTION_STEPS.REVIEW);
    }
  }, [selectedSubscription, goToStep]);

  // Effect 2: apply whichever path produced an answer.
  useEffect(() => {
    if (hasApplied.current || selectedSubscription) return;

    // No param, or a malformed one: terminal, nothing to do.
    if (!planParam || !parsed) {
      hasApplied.current = true;
      return;
    }

    if (listLoading) return;

    if (listMatch) {
      setSelectedTab(parsed.type === 'membership' ? 'memberships' : 'passes');
      setSelectedSubscription(
        parsed.type === 'membership'
          ? formatMembershipPlan(listMatch as Parameters<typeof formatMembershipPlan>[0])
          : formatPassPlan(listMatch as Parameters<typeof formatPassPlan>[0]),
      );
      hasApplied.current = true;
      shouldAdvance.current = true;
      return;
    }

    if (!detailSettled) return;

    if (fetchedPlan) {
      setSelectedTab(fetchedPlan.subscriptionType === 'membership' ? 'memberships' : 'passes');
      setSelectedSubscription(fetchedPlan);
      shouldAdvance.current = true;
    }
    // Terminal either way: a 404 stays on the selection step, silently. Marking
    // it applied HERE — not only on success — is what stops a miss re-running on
    // every dependency change.
    hasApplied.current = true;
  }, [
    planParam,
    parsed,
    listLoading,
    listMatch,
    detailSettled,
    fetchedPlan,
    selectedSubscription,
    setSelectedSubscription,
    setSelectedTab,
  ]);

  // Derived from state only — never from `hasApplied`. A ref does not trigger a
  // re-render, so reading it here would hand the step a stale answer on exactly
  // the render that matters. Once a plan is selected, or both sources have gone
  // quiet, there is nothing left to resolve.
  return {
    isResolving: !!parsed && !selectedSubscription && (listLoading || detailPending),
  };
}
